import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';
// import { ChevronLeft, Search, Database, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function SchemaDetailsPage() {
    const [schema, setSchema] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTable, setSelectedTable] = useState(null);
    const itemsPerPage = 20;
    const navigate = useNavigate();

    useEffect(() => {
        loadFullSchema();
    }, []);

    const loadFullSchema = async () => {
        try {
            const token = getToken();
            const res = await axios.get('http://localhost:8001/full-schema', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSchema(res.data.tables || []);
        } catch (err) {
            console.error('Error loading schema:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredTables = schema.filter(table => 
        table.table.toLowerCase().includes(search.toLowerCase())
    );

    const totalPages = Math.ceil(filteredTables.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentTables = filteredTables.slice(startIndex, endIndex);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-4">
                        <div className="flex items-center">
                            <button
                                onClick={() => navigate(-1)}
                                className="mr-4 p-2 hover:bg-gray-100 rounded"
                            >
                                <span>←</span>
                            </button>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Database Schema Details
                            </h1>
                        </div>
                        <div className="text-sm text-gray-500">
                            {filteredTables.length} tables found
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
                        <input
                            type="text"
                            placeholder="Search tables..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {/* Tables Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {currentTables.map((table) => (
                        <div
                            key={table.table}
                            onClick={() => setSelectedTable(table)}
                            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer p-4"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-medium text-gray-900 flex items-center">
                                    <span className="mr-2 text-gray-400">📊</span>
                                    {table.table}
                                </h3>
                                {table.has_foreign_keys && (
                                    <span className="text-green-500">🔑</span>
                                )}
                            </div>
                            <p className="text-sm text-gray-500">
                                {table.column_count} columns
                            </p>
                            {table.has_primary_key && (
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                                    Has Primary Key
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center space-x-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            const pageNum = i + 1;
                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className={`px-3 py-1 rounded ${
                                        currentPage === pageNum
                                            ? 'bg-blue-600 text-white'
                                            : 'border hover:bg-gray-50'
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        {totalPages > 5 && <span>...</span>}
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Table Details Modal */}
            {selectedTable && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setSelectedTable(null)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b px-6 py-4">
                                <h3 className="text-lg font-semibold">{selectedTable.table}</h3>
                            </div>
                            <div className="px-6 py-4">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Column</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nullable</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Keys</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {selectedTable.columns.map((col, idx) => (
                                            <tr key={idx}>
                                                <td className="px-3 py-2 text-sm font-medium text-gray-900">{col.name}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{col.type}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">
                                                    {col.nullable ? 'Yes' : 'No'}
                                                </td>
                                                <td className="px-3 py-2 text-sm">
                                                    {col.primary_key && (
                                                        <span className="text-blue-600 mr-2">PK</span>
                                                    )}
                                                    {col.foreign_keys && col.foreign_keys.length > 0 && (
                                                        <span className="text-green-600">
                                                            FK → {col.foreign_keys.join(', ')}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 bg-gray-50">
                                <button
                                    onClick={() => setSelectedTable(null)}
                                    className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}